/**
 * Loan Calculator Block
 * Interactive loan payment calculator with sliders for loan amount,
 * interest rate, and term. Suitable for personal loans, auto loans, etc.
 */

import { registerBlockRenderer, getBlockRendererContext } from '../registry';
import { escapeHtml, createBlockWrapper } from '../utils';

// =============================================================================
// TYPES
// =============================================================================

export interface LoanCalcData {
  /** Loan amount in currency units */
  loanAmount?: number;
  /** Annual interest rate as percentage (e.g., 7.5 for 7.5%) */
  interestRate?: number;
  /** Loan term in years */
  term?: number;
  /** Title for the calculator */
  title?: string;
  /** Currency symbol (default: £) */
  currency?: string;
  /** Min loan amount (default: 1000) */
  minAmount?: number;
  /** Max loan amount (default: 100000) */
  maxAmount?: number;
  /** Min term in years (default: 1) */
  minTerm?: number;
  /** Max term in years (default: 10) */
  maxTerm?: number;
}

// =============================================================================
// TYPE VALIDATION
// =============================================================================

function isValidLoanData(data: unknown): data is LoanCalcData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  // All fields are optional with defaults, but validate types if present
  if (d.loanAmount !== undefined && typeof d.loanAmount !== 'number') return false;
  if (d.interestRate !== undefined && typeof d.interestRate !== 'number') return false;
  if (d.term !== undefined && typeof d.term !== 'number') return false;
  if (d.title !== undefined && typeof d.title !== 'string') return false;
  if (d.currency !== undefined && typeof d.currency !== 'string') return false;
  if (d.minAmount !== undefined && typeof d.minAmount !== 'number') return false;
  if (d.maxAmount !== undefined && typeof d.maxAmount !== 'number') return false;
  if (d.minTerm !== undefined && typeof d.minTerm !== 'number') return false;
  if (d.maxTerm !== undefined && typeof d.maxTerm !== 'number') return false;

  return true;
}

// =============================================================================
// RENDER FUNCTION
// =============================================================================

function renderLoanCalc(data: LoanCalcData, container: HTMLElement): void {
  const wrapper = createBlockWrapper('loan-calc');

  // Get currency from data, fall back to context, then to £
  const context = getBlockRendererContext();
  const currency = data.currency || context.currencySymbol || '£';
  const defaultAmount = data.loanAmount || 15000;
  const defaultRate = data.interestRate || 7.9;
  const defaultTerm = data.term || 5;
  const minAmount = data.minAmount || 1000;
  const maxAmount = data.maxAmount || 100000;
  const minTerm = data.minTerm || 1;
  const maxTerm = data.maxTerm || 10;

  let html = '';

  if (data.title) {
    html += `<h3 class="pms-loan-title">${escapeHtml(data.title)}</h3>`;
  }

  html += '<div class="pms-loan-calc">';

  // Loan Amount
  html += `
    <div class="pms-loan-field">
      <label class="pms-loan-label">Loan Amount</label>
      <div class="pms-loan-value-display">
        <span class="pms-loan-currency">${escapeHtml(currency)}</span>
        <span class="pms-loan-amount" data-display="amount">${defaultAmount.toLocaleString()}</span>
      </div>
      <input type="range" class="pms-loan-slider" data-slider="amount" min="${minAmount}" max="${maxAmount}" step="500" value="${defaultAmount}">
      <div class="pms-loan-range">
        <span>${currency}${minAmount.toLocaleString()}</span>
        <span>${currency}${maxAmount.toLocaleString()}</span>
      </div>
    </div>
  `;

  // Interest Rate
  html += `
    <div class="pms-loan-field">
      <label class="pms-loan-label">Interest Rate (APR)</label>
      <div class="pms-loan-value-display">
        <span class="pms-loan-amount" data-display="rate">${defaultRate.toFixed(1)}</span>
        <span class="pms-loan-suffix">%</span>
      </div>
      <input type="range" class="pms-loan-slider" data-slider="rate" min="1" max="30" step="0.1" value="${defaultRate}">
      <div class="pms-loan-range">
        <span>1%</span>
        <span>30%</span>
      </div>
    </div>
  `;

  // Term
  html += `
    <div class="pms-loan-field">
      <label class="pms-loan-label">Loan Term</label>
      <div class="pms-loan-value-display">
        <span class="pms-loan-amount" data-display="term">${defaultTerm}</span>
        <span class="pms-loan-suffix">years</span>
      </div>
      <input type="range" class="pms-loan-slider" data-slider="term" min="${minTerm}" max="${maxTerm}" step="1" value="${defaultTerm}">
      <div class="pms-loan-range">
        <span>${minTerm} year${minTerm > 1 ? 's' : ''}</span>
        <span>${maxTerm} years</span>
      </div>
    </div>
  `;

  // Results
  html += `
    <div class="pms-loan-results">
      <div class="pms-loan-result-main">
        <span class="pms-loan-result-label">Monthly Payment</span>
        <span class="pms-loan-result-value" data-display="monthly">${currency}0</span>
      </div>
      <div class="pms-loan-result-row">
        <div class="pms-loan-result-item">
          <span class="pms-loan-result-label">Total Repayment</span>
          <span class="pms-loan-result-value" data-display="total">${currency}0</span>
        </div>
        <div class="pms-loan-result-item">
          <span class="pms-loan-result-label">Total Interest</span>
          <span class="pms-loan-result-value" data-display="interest">${currency}0</span>
        </div>
      </div>
    </div>
  `;

  html += '</div>';
  wrapper.innerHTML = html;

  // Get elements
  const amountSlider = wrapper.querySelector('[data-slider="amount"]') as HTMLInputElement;
  const rateSlider = wrapper.querySelector('[data-slider="rate"]') as HTMLInputElement;
  const termSlider = wrapper.querySelector('[data-slider="term"]') as HTMLInputElement;

  const amountDisplay = wrapper.querySelector('[data-display="amount"]') as HTMLElement;
  const rateDisplay = wrapper.querySelector('[data-display="rate"]') as HTMLElement;
  const termDisplay = wrapper.querySelector('[data-display="term"]') as HTMLElement;
  const monthlyDisplay = wrapper.querySelector('[data-display="monthly"]') as HTMLElement;
  const totalDisplay = wrapper.querySelector('[data-display="total"]') as HTMLElement;
  const interestDisplay = wrapper.querySelector('[data-display="interest"]') as HTMLElement;

  function formatCurrency(value: number): string {
    return currency + value.toLocaleString('en-GB', { maximumFractionDigits: 0 });
  }

  function calculate(): void {
    const amount = parseFloat(amountSlider.value) || 0;
    const rate = parseFloat(rateSlider.value) || 0;
    const term = parseFloat(termSlider.value) || 1;

    // Update displays
    amountDisplay.textContent = amount.toLocaleString();
    rateDisplay.textContent = rate.toFixed(1);
    termDisplay.textContent = String(term);

    // Monthly interest rate
    const monthlyRate = rate / 100 / 12;
    const numPayments = term * 12;

    // Monthly payment formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    let monthlyPayment = 0;
    if (monthlyRate > 0 && numPayments > 0) {
      const factor = Math.pow(1 + monthlyRate, numPayments);
      monthlyPayment = amount * (monthlyRate * factor) / (factor - 1);
    } else if (numPayments > 0) {
      monthlyPayment = amount / numPayments;
    }

    const totalRepayment = monthlyPayment * numPayments;
    const totalInterest = totalRepayment - amount;

    // Update result displays
    monthlyDisplay.textContent = formatCurrency(Math.round(monthlyPayment));
    totalDisplay.textContent = formatCurrency(Math.round(totalRepayment));
    interestDisplay.textContent = formatCurrency(Math.round(totalInterest));
  }

  // Add event listeners
  amountSlider.addEventListener('input', calculate);
  rateSlider.addEventListener('input', calculate);
  termSlider.addEventListener('input', calculate);

  // Initial calculation
  calculate();

  container.appendChild(wrapper);
}

// =============================================================================
// REGISTRATION
// =============================================================================

registerBlockRenderer('loan-calc', (data: unknown, container: HTMLElement) => {
  if (!isValidLoanData(data)) {
    console.warn('[loan-calc] Invalid data:', data);
    const wrapper = createBlockWrapper('loan-calc');
    wrapper.innerHTML = '<div class="pms-block-error">Invalid loan calculator data</div>';
    container.appendChild(wrapper);
    return;
  }

  renderLoanCalc(data, container);
});
